import React, { useEffect, useState } from 'react';
import {
  getCampaigns, getJuryMembers, addJuryMember, updateJuryMember, removeJuryMember,
  getJurySelection, setJurySelection,
  getLegalAcceptances, getLegalAcceptancesExportUrl,
  getJuryResults, setJuryVoteLock, getJuryResultsExportUrl,
} from '../../api';
import { useToast } from '../../components/Toast';
import JuryResultsBoard from '../../components/JuryResultsBoard';

// Une valeur NULL en base signifie "aucune borne" : l'acces est alors ouvert.
// On affiche donc explicitement "illimité" plutot qu'une case vide ambigue.
const fmtDate = (v) => (v ? new Date(v).toLocaleDateString('fr-FR') : 'illimité');
// <input type="date"> attend 'YYYY-MM-DD' ; la base renvoie un timestamp ISO.
const toDateInput = (v) => (v ? String(v).slice(0, 10) : '');

const fullName = (u) => {
  const n = `${u.first_name || ''} ${u.last_name || ''}`.trim();
  return n || u.email;
};

// Couleurs de la barre de score, identiques au classement ISI de l'espace jury.
const barGradient = (pct) => (
  pct > 70 ? 'linear-gradient(90deg, #27AE60, #2ECC71)'
    : pct > 40 ? 'linear-gradient(90deg, #F39C12, #F1C40F)'
      : 'linear-gradient(90deg, #E74C3C, #E67E22)'
);

// Recalcule l'objet selection apres bascule de cases, compteurs inclus.
// visible_count ne compte que les startups cochees ET dont le dossier est
// soumis : c'est le nombre reellement visible par le jury.
const applyVisible = (sel, ids, visible) => {
  if (!sel) return sel;
  const set = new Set(ids);
  const startups = sel.startups.map(s => (
    set.has(s.startup_id) ? { ...s, jury_visible: visible } : s
  ));
  return {
    ...sel,
    startups,
    selected_count: startups.filter(s => s.jury_visible).length,
    visible_count: startups.filter(s => s.jury_visible && s.submitted).length,
  };
};

export default function AdminJuryMembers() {
  const toast = useToast();
  const [campaigns, setCampaigns] = useState([]);
  const [campaignId, setCampaignId] = useState('');
  const [data, setData] = useState({ members: [], available_users: [] });
  const [loading, setLoading] = useState(true);
  const [addForm, setAddForm] = useState({ user_id: '', access_start: '', access_end: '' });
  const [saving, setSaving] = useState(false);
  const [editing, setEditing] = useState(null); // { id, access_start, access_end }

  // Selection des startups soumises au jury
  const [selection, setSelection] = useState(null);
  const [selLoading, setSelLoading] = useState(true);
  const [togglingId, setTogglingId] = useState(null);
  const [bulkBusy, setBulkBusy] = useState(false);
  const [selSearch, setSelSearch] = useState('');

  // Engagements de confidentialité signés (indépendants de la campagne : c'est
  // l'accord d'une personne sur un texte, pas sur une édition des Trophées).
  const [legal, setLegal] = useState(null);
  const [legalError, setLegalError] = useState('');

  // Dépouillement du vote du jury (portefeuilles virtuels).
  const [results, setResults] = useState(null);
  const [resultsError, setResultsError] = useState('');
  const [lockBusy, setLockBusy] = useState(false);
  const [showMatrix, setShowMatrix] = useState(false);

  const loadResults = (id) => {
    if (!id) return;
    setResultsError('');
    getJuryResults(id).then(setResults).catch(err => { setResults(null); setResultsError(err.message); });
  };

  useEffect(() => { loadResults(campaignId); }, [campaignId]);

  const handleToggleLock = async () => {
    const closing = !results.locked;
    const ok = await toast.confirm(closing
      ? 'Clôturer le vote du jury ?\n\nLes jurés ne pourront plus modifier leur portefeuille. '
        + 'Le classement sera figé.'
      : 'Rouvrir le vote ?\n\nLes jurés pourront de nouveau modifier leurs choix, et le '
        + 'classement pourra changer.');
    if (!ok) return;
    setLockBusy(true);
    try {
      await setJuryVoteLock(campaignId, closing);
      loadResults(campaignId);
      toast.success(closing ? 'Vote clôturé' : 'Vote rouvert');
    } catch (err) { toast.error(err.message); }
    setLockBusy(false);
  };

  const handleExportResults = async () => {
    try {
      const token = localStorage.getItem('isi_token');
      const resp = await fetch(getJuryResultsExportUrl(campaignId), {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!resp.ok) throw new Error('HTTP ' + resp.status);
      const blob = await resp.blob();
      const a = document.createElement('a');
      a.href = URL.createObjectURL(blob);
      a.download = `vote_jury_${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(a); a.click(); a.remove();
      toast.success('Export CSV téléchargé');
    } catch (err) { toast.error('Erreur export : ' + err.message); }
  };

  useEffect(() => {
    getLegalAcceptances().then(setLegal).catch(err => setLegalError(err.message));
  }, []);

  const handleExportLegal = async () => {
    try {
      const token = localStorage.getItem('isi_token');
      const resp = await fetch(getLegalAcceptancesExportUrl(), {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!resp.ok) throw new Error('HTTP ' + resp.status);
      const blob = await resp.blob();
      const a = document.createElement('a');
      a.href = URL.createObjectURL(blob);
      a.download = `engagements_confidentialite_${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(a); a.click(); a.remove();
      toast.success('Export CSV téléchargé');
    } catch (err) { toast.error('Erreur export : ' + err.message); }
  };

  useEffect(() => {
    getCampaigns()
      .then(cs => {
        setCampaigns(cs);
        if (cs.length > 0) setCampaignId(String(cs[0].id));
        else setLoading(false);
      })
      .catch(err => { toast.error(err.message); setLoading(false); });
  }, []);

  const load = (id) => {
    setLoading(true);
    getJuryMembers(id)
      .then(setData)
      .catch(err => toast.error(err.message))
      .finally(() => setLoading(false));
  };

  const loadSelection = (id) => {
    setSelLoading(true);
    getJurySelection(id)
      .then(setSelection)
      .catch(err => { toast.error(err.message); setSelection(null); })
      .finally(() => setSelLoading(false));
  };

  useEffect(() => {
    if (campaignId) { load(campaignId); loadSelection(campaignId); }
  }, [campaignId]);

  // Mise a jour optimiste : la case bascule immediatement, et n'est remise
  // dans son etat initial que si l'appel echoue.
  const handleToggle = async (s) => {
    const next = !s.jury_visible;
    setTogglingId(s.startup_id);
    setSelection(sel => applyVisible(sel, [s.startup_id], next));
    try {
      await setJurySelection(campaignId, [s.startup_id], next);
    } catch (err) {
      setSelection(sel => applyVisible(sel, [s.startup_id], !next));
      toast.error(err.message);
    }
    setTogglingId(null);
  };

  const handleBulk = async (visible) => {
    // Porte sur les lignes affichees, pour que le bouton reste coherent
    // avec la recherche en cours.
    const ids = visibleRows.filter(s => s.jury_visible !== visible).map(s => s.startup_id);
    if (ids.length === 0) return;
    setBulkBusy(true);
    const before = selection;
    setSelection(sel => applyVisible(sel, ids, visible));
    try {
      await setJurySelection(campaignId, ids, visible);
      toast.success(visible
        ? `${ids.length} startup(s) rendues visibles au jury`
        : `${ids.length} startup(s) retirees de la selection`);
    } catch (err) {
      setSelection(before);
      toast.error(err.message);
    }
    setBulkBusy(false);
  };

  const handleAdd = async (e) => {
    e.preventDefault();
    if (!addForm.user_id) { toast.error('Sélectionnez un compte jury'); return; }
    setSaving(true);
    try {
      await addJuryMember({
        campaign_id: parseInt(campaignId, 10),
        user_id: parseInt(addForm.user_id, 10),
        access_start: addForm.access_start || null,
        access_end: addForm.access_end || null,
      });
      setAddForm({ user_id: '', access_start: '', access_end: '' });
      load(campaignId);
      toast.success('Membre du jury ajouté');
    } catch (err) { toast.error(err.message); }
    setSaving(false);
  };

  const handleSaveAccess = async (m) => {
    setSaving(true);
    try {
      await updateJuryMember(m.id, {
        access_start: editing.access_start || null,
        access_end: editing.access_end || null,
      });
      setEditing(null);
      load(campaignId);
      toast.success('Créneau d\'accès mis à jour');
    } catch (err) { toast.error(err.message); }
    setSaving(false);
  };

  const handleRemove = async (m) => {
    if (!window.confirm(
      `Retirer ${fullName(m)} du jury de cette campagne ?\n\n`
      + `Le compte utilisateur n'est pas supprimé : la personne perd seulement `
      + `l'accès à cette campagne.\n\n`
      + `Si elle avait voté, son portefeuille est conservé mais cesse d'être compté `
      + `dans le dépouillement. La rattacher de nouveau lui rendrait sa voix.`
    )) return;
    try {
      await removeJuryMember(m.id);
      load(campaignId);
      toast.success('Membre retiré du jury');
    } catch (err) { toast.error(err.message); }
  };

  const activeCampaign = campaigns.find(c => String(c.id) === String(campaignId));

  const q = selSearch.trim().toLowerCase();
  const visibleRows = !selection ? [] : selection.startups.filter(s => (
    !q || (s.name || '').toLowerCase().includes(q) || (s.secteur || '').toLowerCase().includes(q)
  ));

  return (
    <div style={{ padding: '32px 0' }}>
      <div className="container">
        <h1 style={{ marginBottom: 8 }}>Membres du Jury</h1>
        <p style={{ color: 'var(--text-light)', fontSize: 14, marginBottom: 24 }}>
          Rattachez les comptes jury à une campagne. Un membre du jury ne voit que les
          campagnes auxquelles il est rattaché, et uniquement les startups participantes
          <strong> ayant soumis leur dossier</strong>.
        </p>

        {campaigns.length === 0 && !loading ? (
          <div className="card" style={{ textAlign: 'center', padding: 32 }}>
            Aucune campagne. Créez-en une depuis l'onglet « Trophées ».
          </div>
        ) : (
          <>
            <div className="card" style={{ marginBottom: 20, padding: 16 }}>
              <label className="form-label">Campagne</label>
              <select value={campaignId} onChange={e => setCampaignId(e.target.value)}
                className="form-input" style={{ maxWidth: 380, fontSize: 14 }}>
                {campaigns.map(c => (
                  <option key={c.id} value={c.id}>
                    {c.name} — {c.year}{c.is_active ? ' (active)' : ''}
                  </option>
                ))}
              </select>
            </div>

            {/* ----- Ajout d'un membre ----- */}
            <div className="card" style={{ marginBottom: 20 }}>
              <h3 style={{ marginBottom: 16, fontSize: 16 }}>Ajouter un membre du jury</h3>
              {data.available_users.length === 0 ? (
                <p style={{ fontSize: 13, color: 'var(--text-light)', margin: 0 }}>
                  Tous les comptes jury existants sont déjà rattachés à cette campagne.
                  Créez un nouveau compte avec le rôle « Membre du Jury » depuis
                  l'onglet « Utilisateurs ».
                </p>
              ) : (
                <form onSubmit={handleAdd}>
                  <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr auto', gap: 12, alignItems: 'end' }}>
                    <div>
                      <label className="form-label">Compte jury</label>
                      <select value={addForm.user_id}
                        onChange={e => setAddForm(f => ({ ...f, user_id: e.target.value }))}
                        className="form-input" required>
                        <option value="">— Sélectionner —</option>
                        {data.available_users.map(u => (
                          <option key={u.id} value={u.id}>
                            {fullName(u)} ({u.email}){u.is_active ? '' : ' — compte inactif'}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className="form-label">Accès à partir du</label>
                      <input type="date" value={addForm.access_start}
                        onChange={e => setAddForm(f => ({ ...f, access_start: e.target.value }))}
                        className="form-input" />
                    </div>
                    <div>
                      <label className="form-label">Accès jusqu'au</label>
                      <input type="date" value={addForm.access_end}
                        onChange={e => setAddForm(f => ({ ...f, access_end: e.target.value }))}
                        className="form-input" />
                    </div>
                    <button type="submit" className="btn btn-accent" disabled={saving}>
                      {saving ? '...' : 'Ajouter'}
                    </button>
                  </div>
                  <p style={{ fontSize: 12, color: 'var(--text-light)', marginTop: 10, marginBottom: 0 }}>
                    Dates optionnelles : laissées vides, l'accès est ouvert sans limite de date.
                  </p>
                </form>
              )}
            </div>

            {/* ----- Liste des membres ----- */}
            <div className="card">
              <h3 style={{ marginBottom: 16, fontSize: 16 }}>
                Jury de {activeCampaign ? activeCampaign.name : 'la campagne'}
              </h3>
              {loading ? (
                <div className="loading">Chargement...</div>
              ) : data.members.length === 0 ? (
                <p style={{ fontSize: 13, color: 'var(--text-light)', margin: 0 }}>
                  Aucun membre du jury rattaché à cette campagne. Tant qu'un juré n'est pas
                  rattaché, son espace jury affichera « Aucune campagne accessible ».
                </p>
              ) : (
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <thead>
                    <tr style={{ borderBottom: '2px solid var(--border)' }}>
                      <th style={{ padding: 12, textAlign: 'left' }}>Membre</th>
                      <th style={{ padding: 12, textAlign: 'left' }}>Email</th>
                      <th style={{ padding: 12, textAlign: 'center' }}>Compte</th>
                      <th style={{ padding: 12, textAlign: 'center' }}>Accès</th>
                      <th style={{ padding: 12, textAlign: 'center' }}>Dernière connexion</th>
                      <th style={{ padding: 12, textAlign: 'center' }}>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.members.map(m => (
                      <tr key={m.id} style={{ borderBottom: '1px solid var(--border)' }}>
                        <td style={{ padding: 12, fontWeight: 500 }}>
                          {fullName(m)}
                          {m.company && (
                            <div style={{ fontSize: 12, color: 'var(--text-light)' }}>{m.company}</div>
                          )}
                        </td>
                        <td style={{ padding: 12, fontSize: 13 }}>{m.email}</td>
                        <td style={{ padding: 12, textAlign: 'center' }}>
                          <span className={`badge badge-${m.is_active ? 'vert' : 'rouge'}`} style={{ fontSize: 11 }}>
                            {m.is_active ? 'Actif' : 'Inactif'}
                          </span>
                        </td>
                        <td style={{ padding: 12, textAlign: 'center', fontSize: 13 }}>
                          {editing && editing.id === m.id ? (
                            <div style={{ display: 'flex', gap: 6, justifyContent: 'center' }}>
                              <input type="date" value={editing.access_start}
                                onChange={e => setEditing(s => ({ ...s, access_start: e.target.value }))}
                                className="form-input" style={{ fontSize: 12, padding: '4px 6px', width: 130 }} />
                              <input type="date" value={editing.access_end}
                                onChange={e => setEditing(s => ({ ...s, access_end: e.target.value }))}
                                className="form-input" style={{ fontSize: 12, padding: '4px 6px', width: 130 }} />
                            </div>
                          ) : (
                            <span>{fmtDate(m.access_start)} → {fmtDate(m.access_end)}</span>
                          )}
                        </td>
                        <td style={{ padding: 12, textAlign: 'center', fontSize: 12, color: 'var(--text-light)' }}>
                          {m.last_login ? new Date(m.last_login).toLocaleDateString('fr-FR') : 'jamais'}
                        </td>
                        <td style={{ padding: 12, textAlign: 'center' }}>
                          <div style={{ display: 'flex', gap: 6, justifyContent: 'center' }}>
                            {editing && editing.id === m.id ? (
                              <>
                                <button onClick={() => handleSaveAccess(m)} disabled={saving}
                                  style={{ padding: '4px 12px', border: '1px solid #A5D6A7', borderRadius: 6,
                                    background: '#F1F8E9', cursor: 'pointer', fontSize: 11, fontFamily: 'inherit',
                                    color: '#2E7D32', fontWeight: 600 }}>
                                  Enregistrer
                                </button>
                                <button onClick={() => setEditing(null)}
                                  style={{ padding: '4px 12px', border: '1px solid var(--border)', borderRadius: 6,
                                    background: 'white', cursor: 'pointer', fontSize: 11, fontFamily: 'inherit' }}>
                                  Annuler
                                </button>
                              </>
                            ) : (
                              <>
                                <button onClick={() => setEditing({
                                  id: m.id,
                                  access_start: toDateInput(m.access_start),
                                  access_end: toDateInput(m.access_end),
                                })}
                                  style={{ padding: '4px 12px', border: '1px solid var(--border)', borderRadius: 6,
                                    background: 'white', cursor: 'pointer', fontSize: 11, fontFamily: 'inherit',
                                    color: 'var(--primary)' }}>
                                  Modifier l'accès
                                </button>
                                <button onClick={() => handleRemove(m)}
                                  style={{ padding: '4px 12px', border: '1px solid #FFCDD2', borderRadius: 6,
                                    background: '#FFF5F5', cursor: 'pointer', fontSize: 11, fontFamily: 'inherit',
                                    color: '#E74C3C', fontWeight: 600 }}>
                                  Retirer
                                </button>
                              </>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>

            {/* ----- Depouillement du vote du jury ----- */}
            <div className="card" style={{ marginTop: 24 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                gap: 12, flexWrap: 'wrap', marginBottom: 8 }}>
                <h3 style={{ margin: 0 }}>Résultat du vote du jury</h3>
                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                  <button onClick={handleExportResults} className="btn btn-outline"
                    style={{ fontSize: 13 }} disabled={!results}>Export CSV</button>
                  {results && (
                    <button onClick={handleToggleLock} disabled={lockBusy}
                      className={results.locked ? 'btn btn-outline' : 'btn btn-accent'}
                      style={{ fontSize: 13 }}>
                      {results.locked ? 'Rouvrir le vote' : 'Clôturer le vote'}
                    </button>
                  )}
                </div>
              </div>
              {/* Le bloc commun (règles, participation, podium, classement) vit
                  dans JuryResultsBoard : le jury voit exactement le même après
                  clôture. Ne restent ici que les parties réservées à
                  l'organisateur. */}
              <JuryResultsBoard results={results} error={resultsError} />
              {results && (
                <>
                  {/* Coups de cœur : distinction séparée du capital. */}
                  {results.favorites.length > 0 && (
                  <div style={{ marginBottom: 20 }}>
                    <h4 style={{ marginBottom: 8 }}>❤️ Coups de cœur</h4>
                    <div style={{ display: 'grid', gap: 6 }}>
                      {results.favorites.map(f => (
                        <div key={f.startup_id} style={{ display: 'flex', alignItems: 'center', gap: 10,
                          padding: '8px 12px', borderRadius: 8, background: 'var(--bg-secondary)',
                          fontSize: 13, flexWrap: 'wrap' }}>
                          <strong>{f.name}</strong>
                          <span style={{ color: '#C0392B', fontWeight: 700 }}>
                            {f.votes} voix
                          </span>
                          <span style={{ color: 'var(--text-light)', fontSize: 12 }}>
                            {f.jures.join(', ')}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                  )}

                  {/* Détail par juré : qui a mis quoi. */}
                  <button onClick={() => setShowMatrix(v => !v)} className="btn btn-outline"
                    style={{ fontSize: 13, marginBottom: showMatrix ? 14 : 0 }}>
                    {showMatrix ? 'Masquer le détail par juré' : 'Voir le détail par juré'}
                  </button>

                  {showMatrix && (
                    <div className="table-scroll">
                      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12.5 }}>
                        <thead>
                          <tr style={{ borderBottom: '2px solid var(--border)' }}>
                            <th style={{ textAlign: 'left', padding: '8px 10px', position: 'sticky', left: 0,
                              background: 'var(--card)' }}>Juré</th>
                            {results.ranking.map(r => (
                              <th key={r.startup_id} style={{ padding: '8px 6px', textAlign: 'center',
                                fontSize: 11, maxWidth: 90 }}>{r.name}</th>
                            ))}
                            <th style={{ padding: '8px 10px', textAlign: 'right' }}>Total</th>
                          </tr>
                        </thead>
                        <tbody>
                          {results.members.map(m => {
                            const byStartup = {};
                            results.detail.filter(d => d.user_id === m.user_id)
                              .forEach(d => { byStartup[d.startup_id] = d.amount; });
                            return (
                              <tr key={m.user_id} style={{ borderBottom: '1px solid var(--border)' }}>
                                <td style={{ padding: '8px 10px', position: 'sticky', left: 0,
                                  background: 'var(--card)' }}>
                                  <div style={{ fontWeight: 600 }}>{m.name}</div>
                                  <div style={{ fontSize: 11,
                                    color: m.complete ? '#1E8449' : '#8D6E20' }}>
                                    {m.complete ? 'complet' : m.invested > 0 ? 'brouillon' : 'n\'a pas voté'}
                                  </div>
                                </td>
                                {results.ranking.map(r => (
                                  <td key={r.startup_id} style={{ padding: '8px 6px', textAlign: 'center',
                                    fontWeight: byStartup[r.startup_id] ? 700 : 400,
                                    color: byStartup[r.startup_id] ? '#C8880A' : 'var(--border-strong)',
                                    background: byStartup[r.startup_id]
                                      ? `rgba(224,161,6,${0.06 + 0.1 * byStartup[r.startup_id]})` : 'transparent' }}>
                                    {byStartup[r.startup_id] || '·'}
                                  </td>
                                ))}
                                <td style={{ padding: '8px 10px', textAlign: 'right', fontWeight: 700,
                                  color: m.invested === 15 ? '#1E8449' : 'var(--text-light)' }}>
                                  {m.invested}
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  )}
                </>
              )}
            </div>

            {/* ----- Engagements de confidentialite signes ----- */}
            <div className="card" style={{ marginTop: 24 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                gap: 12, flexWrap: 'wrap', marginBottom: 8 }}>
                <h3 style={{ margin: 0 }}>Engagements de confidentialité</h3>
                <button onClick={handleExportLegal} className="btn btn-outline"
                  style={{ fontSize: 13 }} disabled={!legal || legal.acceptances.length === 0}>
                  Export CSV
                </button>
              </div>
              <p style={{ fontSize: 13, color: 'var(--text-light)', marginBottom: 16 }}>
                Accord recueilli à la première connexion de chaque membre du jury. Chaque ligne
                porte la <strong>version et l'empreinte du texte</strong> réellement affiché : c'est
                ce qui rattache l'accord à un contenu précis en cas de contestation.
              </p>

              {legalError ? (
                <div style={{ padding: '10px 14px', borderRadius: 8, background: '#FFEBEE',
                  color: '#C62828', fontSize: 13 }}>{legalError}</div>
              ) : !legal ? (
                <div className="loading">Chargement des engagements...</div>
              ) : (
                <>
                  <div style={{ fontSize: 12.5, color: 'var(--text-light)', marginBottom: 12 }}>
                    Texte en vigueur : version <strong>{legal.document.version}</strong> ·
                    empreinte <code style={{ fontSize: 11 }}>{legal.document.hash.slice(0, 16)}…</code>
                  </div>

                  {legal.pending.length > 0 && (
                    <div style={{ padding: '10px 14px', marginBottom: 14, borderRadius: 8,
                      background: '#FFF8E1', border: '1px solid #FFE082', fontSize: 13, color: '#8D6E20' }}>
                      <strong>{legal.pending.length}</strong> membre(s) n'ont pas encore accepté :{' '}
                      {legal.pending.map(p => p.name || p.email).join(', ')}. L'écran leur sera
                      présenté à leur prochaine connexion.
                    </div>
                  )}

                  {legal.acceptances.length === 0 ? (
                    <p style={{ fontSize: 13, color: 'var(--text-light)', margin: 0 }}>
                      Aucun engagement enregistré pour l'instant.
                    </p>
                  ) : (
                    <div className="table-scroll">
                      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                        <thead>
                          <tr style={{ borderBottom: '2px solid var(--border)' }}>
                            <th style={{ textAlign: 'left', padding: '8px 10px' }}>Membre</th>
                            <th style={{ textAlign: 'left', padding: '8px 10px' }}>Accepté le</th>
                            <th style={{ textAlign: 'left', padding: '8px 10px' }}>Version</th>
                            <th style={{ textAlign: 'left', padding: '8px 10px' }}>Empreinte</th>
                            <th style={{ textAlign: 'left', padding: '8px 10px' }}>Origine</th>
                          </tr>
                        </thead>
                        <tbody>
                          {legal.acceptances.map(a => (
                            <tr key={a.id} style={{ borderBottom: '1px solid var(--border)' }}>
                              <td style={{ padding: '8px 10px' }}>
                                <div style={{ fontWeight: 600 }}>{a.name || '—'}</div>
                                <div style={{ fontSize: 12, color: 'var(--text-light)' }}>{a.email}</div>
                              </td>
                              <td style={{ padding: '8px 10px', whiteSpace: 'nowrap' }}>
                                {new Date(a.accepted_at).toLocaleString('fr-FR')}
                              </td>
                              <td style={{ padding: '8px 10px' }}>
                                {a.version}
                                {!a.current_version && (
                                  <span style={{ marginLeft: 6, fontSize: 11, color: '#8D6E20' }}>
                                    (version antérieure)
                                  </span>
                                )}
                              </td>
                              <td style={{ padding: '8px 10px', fontFamily: 'monospace', fontSize: 11 }}
                                title={a.hash}>
                                {a.hash.slice(0, 16)}…
                              </td>
                              <td style={{ padding: '8px 10px', fontSize: 12, color: 'var(--text-light)' }}>
                                {a.ip_address || '—'}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </>
              )}
            </div>

            {/* ----- Selection des startups soumises au jury ----- */}
            <div className="card" style={{ marginTop: 24 }}>
              <h3 style={{ marginBottom: 8 }}>
                Startups soumises au jury — {activeCampaign ? activeCampaign.name : 'la campagne'}
              </h3>
              <p style={{ fontSize: 13, color: 'var(--text-light)', marginBottom: 16 }}>
                Cochez les startups retenues pour la <strong>délibération</strong> : elles seules
                apparaissent dans l'onglet Sélection, le classement ISI et la carte de France.
                Les autres candidatures soumises restent <strong>consultables</strong> par le jury
                dans l'onglet Participants, fiches et pièces comprises.
              </p>

              {selLoading ? (
                <div className="loading">Chargement de la sélection...</div>
              ) : !selection || selection.total === 0 ? (
                <p style={{ fontSize: 13, color: 'var(--text-light)', margin: 0 }}>
                  Aucune startup ne participe à cette campagne. Les participations se gèrent
                  depuis l'onglet « Trophées ».
                </p>
              ) : (
                <>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12, alignItems: 'center',
                    justifyContent: 'space-between', marginBottom: 16 }}>
                    <div style={{ fontSize: 13, color: 'var(--text-light)' }}>
                      {selection.total} startups participantes · Note moyenne :{' '}
                      <strong>{selection.average.toFixed(2)}/10</strong> ·{' '}
                      <strong style={{ color: 'var(--accent)' }}>{selection.selected_count}</strong> sélectionnée(s),
                      dont <strong style={{ color: selection.visible_count > 0 ? '#27AE60' : '#E74C3C' }}>
                        {selection.visible_count}
                      </strong> réellement visible(s) par le jury
                    </div>
                    <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                      <input type="text" value={selSearch} placeholder="Rechercher une startup..."
                        onChange={e => setSelSearch(e.target.value)}
                        className="form-input" style={{ fontSize: 13, padding: '6px 10px', width: 220 }} />
                      <button onClick={() => handleBulk(true)} disabled={bulkBusy}
                        style={{ padding: '6px 12px', border: '1px solid #A5D6A7', borderRadius: 6,
                          background: '#F1F8E9', cursor: 'pointer', fontSize: 12, fontFamily: 'inherit',
                          color: '#2E7D32', fontWeight: 600 }}>
                        Tout cocher
                      </button>
                      <button onClick={() => handleBulk(false)} disabled={bulkBusy}
                        style={{ padding: '6px 12px', border: '1px solid var(--border)', borderRadius: 6,
                          background: 'white', cursor: 'pointer', fontSize: 12, fontFamily: 'inherit' }}>
                        Tout décocher
                      </button>
                    </div>
                  </div>

                  {selection.selected_count > 0 && selection.visible_count < selection.selected_count && (
                    <div style={{ padding: '10px 14px', marginBottom: 16, borderRadius: 8, fontSize: 12.5,
                      background: '#FFF8E1', border: '1px solid #FFE082', color: '#8D6E20' }}>
                      {selection.selected_count - selection.visible_count} startup(s) cochée(s) n'ont pas
                      soumis leur dossier : elles resteront invisibles pour le jury jusqu'à la soumission.
                    </div>
                  )}

                  {visibleRows.length === 0 ? (
                    <p style={{ fontSize: 13, color: 'var(--text-light)', margin: 0 }}>
                      Aucune startup ne correspond à « {selSearch} ».
                    </p>
                  ) : visibleRows.map((s) => {
                    // Le rang affiche est celui du classement complet, pas celui
                    // de la liste filtree : il doit rester stable pendant une recherche.
                    const rank = selection.startups.indexOf(s) + 1;
                    const note = s.note_sectorielle;
                    const pct = note !== null ? (note / 10) * 100 : 0;
                    const medal = rank === 1 ? '🥇' : rank === 2 ? '🥈' : rank === 3 ? '🥉' : '';
                    return (
                      <label key={s.startup_id} htmlFor={`jv-${s.startup_id}`}
                        style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 8,
                          cursor: 'pointer', opacity: togglingId === s.startup_id ? 0.5 : 1 }}>
                        <input id={`jv-${s.startup_id}`} type="checkbox" checked={s.jury_visible}
                          onChange={() => handleToggle(s)} disabled={bulkBusy}
                          style={{ width: 17, height: 17, cursor: 'pointer', flexShrink: 0 }} />
                        <span style={{ width: 32, textAlign: 'center', fontWeight: 700, fontSize: 14 }}>
                          {medal || `#${rank}`}
                        </span>
                        <span style={{ width: 180, fontSize: 14, fontWeight: 500, overflow: 'hidden',
                          textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={s.name}>
                          {s.name}
                        </span>
                        {/* Largeur fixe meme sans badge, pour que toutes les
                            barres de score demarrent sur la meme colonne. */}
                        <span style={{ width: 78, flexShrink: 0, textAlign: 'center' }}>
                          {!s.submitted && (
                            <span style={{ fontSize: 10.5, padding: '2px 7px', borderRadius: 10,
                              background: '#FFF3E0', color: '#B26A00', border: '1px solid #FFCC80' }}>
                              non soumis
                            </span>
                          )}
                        </span>
                        <div style={{ flex: 1, height: 24, background: '#F0F0F0', borderRadius: 12, overflow: 'hidden' }}>
                          <div style={{ width: `${pct}%`, height: '100%', borderRadius: 12, background: barGradient(pct) }} />
                        </div>
                        <span style={{ width: 60, textAlign: 'right', fontWeight: 700, fontSize: 15,
                          color: note === null ? 'var(--text-light)' : note > 7 ? '#27AE60' : '#F39C12' }}>
                          {note === null ? '—' : note.toFixed(2)}
                        </span>
                      </label>
                    );
                  })}
                </>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
